import type { ReactElement } from "react";
import { CHARACTER_VOICES, NO_VOICE_LABEL } from "odyssey-shared";
import { KIT_METRICS, Segmented, TextLink, logical, type SegmentedOption } from "../ui/kit/index.ts";
import { say } from "../words.ts";

/**
 * The Voice choice (P1.9-T83, ADR-085's amendment of 2026-10-03; ADR-094 notes 125, 126 and 130): which voice a
 * character's hurt and death play in, on the Look step and in Change look. A field's head row, the label on the left
 * and Preview, a text link, where a field's counter sits, over the kit's segmented choice, the game's voices in their
 * order and None last, as the design card orders them. Preview is the author's choice over the card's "Hear it"
 * button (2026-10-09). In the document the options come before Preview, so Tab reaches the choice and then the link
 * that plays it; the stylesheet's grid lifts Preview to the head row. Preview plays a sample of the voice picked; the
 * choice only reports what was picked and pressed.
 */

/**
 * The option value of having no voice, which no voice's id can be.
 */
const NONE = "";

/**
 * The choice's options: each voice by its label, then None, as the design card orders them.
 */
export const VOICE_OPTIONS: readonly SegmentedOption<string>[] = [...CHARACTER_VOICES.map((voice) => ({ value: voice.id, label: voice.label })), { value: NONE, label: NO_VOICE_LABEL }];

/**
 * What the choice draws and whom it tells: the voice picked, null for none; whether it is held still while a
 * request is out; and the player's pick and their press on Preview.
 */
export interface VoiceChoiceProps {
  readonly value: string | null;
  readonly disabled?: boolean;
  readonly onChange: (voice: string | null) => void;
  readonly onPreview: () => void;
}

/**
 * The Voice choice: its label, its options and Preview, in that order in the document.
 *
 * @param props The voice picked, whether it is disabled, and the player's actions.
 * @returns The choice.
 */
export function VoiceChoice({ value, disabled = false, onChange, onPreview }: VoiceChoiceProps): ReactElement {
  const options = disabled ? VOICE_OPTIONS.map((option) => ({ ...option, disabled: true })) : VOICE_OPTIONS;
  return (
    <div className="ot-voice" data-voice="choice" style={{ rowGap: logical(KIT_METRICS.field.headGap), gridTemplateRows: `${logical(KIT_METRICS.field.head)} auto` }}>
      <span className="ot-voice__label" aria-hidden="true">
        {say("account.voice.label")}
      </span>
      <span className="ot-voice__options">
        <Segmented label={say("account.voice.label")} options={options} value={value ?? NONE} onChange={(picked) => onChange(picked === NONE ? null : picked)} />
      </span>
      <span className="ot-voice__preview" data-voice="preview">
        <TextLink onClick={onPreview} disabled={disabled}>
          {say("account.voice.preview")}
        </TextLink>
      </span>
    </div>
  );
}
